import { FC, useRef, useEffect } from "react";
import { Box, Stack, StackProps } from "@mui/material";
import { useEffectOnceWhen } from "rooks";
import { GroupedTopOpportunities } from "../../../utils/types/types";

import {
  OpportunitiesTopOpportunitiesSelectProps,
  TopOpportunitiesSelect,
} from "@/components/pages/common/opportunities/components/metadata/top-opportunities/TopOpportunitiesSelect";
import { highChartColors } from "@/storybook/c8r-highcharts/common/utils/constants/chartColors";
import { Cube } from "@/storybook/c8r-highcharts/Cube";
import { money } from "@/utils/numeral/money";
import { TopOpportunitiesOtherWithPopover } from "@/components/pages/common/opportunities/components/metadata/top-opportunities/TopOpportunitiesOtherWithPopover";
import { useWindowResizeDebounced } from "@/components/utils/hooks/useWindowResizeDebounced.hook";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { topOpportunityMaxWidth } from "@/components/pages/common/opportunities/utils/constants/common";

interface TopOpportunitiesProps
  extends OpportunitiesTopOpportunitiesSelectProps {
  groupedData?: GroupedTopOpportunities;
  onWidthChange?: (width: number) => void;
  wrapperStackProps?: StackProps;
}

export const TopOpportunities: FC<TopOpportunitiesProps> = ({
  subject,
  groupBy,
  setGroupBy,
  groupedData,

  topOpportunitiesOptions,
  onWidthChange,
  wrapperStackProps,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { rerenderKey } = useWindowResizeDebounced();

  useEffect(() => {
    if (!containerRef.current || rerenderKey < 1) {
      return;
    }

    const width = containerRef.current.offsetWidth;
    if (onWidthChange) {
      onWidthChange(width);
    }
  }, [rerenderKey, onWidthChange]);

  useEffectOnceWhen(() => {
    if (!containerRef.current) {
      return;
    }

    const width = containerRef.current.offsetWidth;
    if (onWidthChange) {
      onWidthChange(width);
    }
  }, !!containerRef?.current?.offsetWidth);

  return (
    <Stack
      p={1.5}
      pt={0.75}
      border={1}
      spacing={0.5}
      borderRadius={2}
      width={wrapperStackProps?.flexBasis ?? "100%"}
      borderColor="grey.300"
      overflow="hidden"
      {...wrapperStackProps}
    >
      <TopOpportunitiesSelect
        groupBy={groupBy}
        subject={subject}
        setGroupBy={setGroupBy}
        topOpportunitiesOptions={topOpportunitiesOptions}
      />

      <Stack ref={containerRef} flex={1}>
        <Stack direction="row" spacing={0.5} flexWrap="nowrap">
          {groupedData?.map(({ percent, name, value }, i) => {
            if (value === 0) {
              return null;
            }

            return (
              <Box
                key={name}
                height={4}
                borderRadius={1}
                width={`${percent}%`}
                bgcolor={highChartColors[i]}
              />
            );
          })}
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="nowrap">
          {groupedData?.map(({ value, name, spread }, i) => {
            if (value === 0) {
              return null;
            }

            return (
              <Stack key={name} maxWidth={topOpportunityMaxWidth}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Cube color={highChartColors[i]} width={12} height={12} />
                  <TypographyWithTooltip
                    title={name}
                    variant="caption"
                    color="text.secondary"
                  />
                </Stack>

                {!spread ? (
                  <TypographyWithTooltip
                    title={money(value)}
                    sx={{ pl: 2.5 }}
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  />
                ) : (
                  <TopOpportunitiesOtherWithPopover
                    value={value}
                    spread={spread}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
};
